import { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Eraser, Undo2, Pencil, Sparkles, Mic2, Play } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { animalAlphabetData } from '../../data/animalAlphabetGrid';
import {
  speakSequence,
  speak,
  speakLetterAnimalAndNature,
  prefetchLetterAnimalAndNature,
  playClickFeedback,
} from '../../lib/naturalVoiceService';

// ── Design tokens — same vocabulary as AnimalAlphabetGrid + the rest ────
const VDC_INK = '#2a6d5b';            // Deep ink green for letter / titles
const VDC_INK_SOFT = '#355c46';
const VDC_BRAND_GREEN = '#42a57a';
const VDC_GOLD = '#c9a961';
const VDC_CREAM = '#fbfbe8';
const VDC_CREAM_DEEP = '#f4efde';
const VDC_CARD_BORDER = '#e8e0c8';
const VDC_MUTED = '#9a9a9a';
const VDC_SOFT_RED = '#b85a55';
const VDC_SERIF = "'Fraunces', Georgia, serif";
const VDC_SANS = "'Inter', system-ui, -apple-system, sans-serif";
/** Pastel cycle keyed by letter so a card always gets the same pastel. */
const VDC_PASTELS = ['#dbeef5', '#fce8d2', '#dcecda', '#fadcdc', '#e8defc', '#d6f0e0'];
const pastelForLetter = (l: string) =>
  VDC_PASTELS[(l.toUpperCase().charCodeAt(0) || 0) % VDC_PASTELS.length];

type LetterOrder = 'alphabetical' | 'random';

interface VoiceDrawingCanvasProps {
  letter: string;
  animal: string;
  emoji?: string;
  funFact?: string;
  onBack: () => void;
  onNext?: () => void;
  language: 'en' | 'es';
  letterOrder?: LetterOrder;
  onLetterOrderChange?: (order: LetterOrder) => void;
}

type DrawMode = 'voice' | 'trace' | 'record';

export function VoiceDrawingCanvas({
  letter,
  animal,
  emoji,
  funFact,
  onBack,
  onNext,
  language,
  letterOrder,
  onLetterOrderChange,
}: VoiceDrawingCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const [drawMode, setDrawMode] = useState<DrawMode>('trace');
  const [isListening, setIsListening] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingBlob, setRecordingBlob] = useState<Blob | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const playbackAudioRef = useRef<HTMLAudioElement | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [volume, setVolume] = useState(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const content = {
    en: {
      voiceTitle: 'Draw with Your Voice!',
      traceTitle: 'Trace the Letter!',
      voiceInstructions: 'Say the letter out loud! Loud = Big, Soft = Small',
      traceInstructions: 'Draw over the letter with your finger or mouse',
      startMic: 'Start Microphone',
      stopMic: 'Stop Microphone',
      voiceMode: 'Voice Drawing',
      traceMode: 'Letter Tracing',
      clear: 'Clear Canvas',
      newLetter: 'Give Me a New Letter',
      back: 'Back',
      alphabetical: 'Alphabetical',
      random: 'Random',
      micPermission: 'Please allow microphone access to use your voice!',
      recordMode: 'Greet your animal friend',
      recordPrompt: 'Say the letter and animal name, then make your best animal sound!',
      recordVoiceEn: (letter: string, animal: string) =>
        `Greet your animal friend! Say the letter ${letter} and ${animal}, then make your best ${animal} sound! When you're ready, tap the microphone to start recording.`,
      startRecord: 'Start Recording',
      stopRecord: 'Stop Recording',
      celebrating: 'Amazing! You did it!',
      animalHappy: 'is so happy to meet you!',
      listenToYou: 'Listen to your voice!',
      funFactLabel: 'Fun fact',
      playRecording: 'Play My Recording',
      recordAgain: 'Record Again',
      close: 'Close',
    },
    es: {
      voiceTitle: '¡Dibuja con tu Voz!',
      traceTitle: '¡Traza la Letra!',
      voiceInstructions: '¡Di la letra en voz alta! Fuerte = Grande, Suave = Pequeño',
      traceInstructions: 'Dibuja sobre la letra con tu dedo o ratón',
      startMic: 'Iniciar Micrófono',
      stopMic: 'Detener Micrófono',
      voiceMode: 'Dibujo con Voz',
      traceMode: 'Trazar Letras',
      clear: 'Limpiar Lienzo',
      newLetter: 'Dame una Letra Nueva',
      back: 'Volver',
      alphabetical: 'Alfabético',
      random: 'Al Azar',
      micPermission: '¡Por favor permite el acceso al micrófono para usar tu voz!',
      recordMode: 'Saluda a tu amigo animal',
      recordPrompt: '¡Di la letra y el nombre del animal, luego haz tu mejor sonido de animal!',
      recordVoiceEs: (letter: string, animal: string) =>
        `¡Saluda a tu amigo animal! Di la letra ${letter} y ${animal}, luego haz tu mejor sonido de ${animal}. Cuando estés listo, toca el micrófono para comenzar a grabar.`,
      startRecord: 'Comenzar Grabación',
      stopRecord: 'Detener Grabación',
      celebrating: '¡Increíble! ¡Lo lograste!',
      animalHappy: '¡está muy feliz de conocerte!',
      listenToYou: '¡Escucha tu voz!',
      funFactLabel: 'Dato curioso',
      playRecording: 'Reproducir Mi Grabación',
      recordAgain: 'Grabar de Nuevo',
      close: 'Cerrar',
    }
  };

  const t = content[language];

  const startListening = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;

      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyserRef.current = analyser;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      setIsListening(true);
      monitorVolume();
    } catch (err) {
      console.error('Microphone access denied:', err);
      alert(t.micPermission);
    }
  };

  const stopListening = () => {
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    setIsListening(false);
    setVolume(0);
  };

  const monitorVolume = () => {
    if (!analyserRef.current) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

    const updateVolume = () => {
      if (!analyserRef.current) return;

      analyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      const normalizedVolume = Math.min(100, (average / 255) * 100);

      setVolume(normalizedVolume);
      drawLetter(normalizedVolume);

      animationFrameRef.current = requestAnimationFrame(updateVolume);
    };

    updateVolume();
  };

  const drawLetter = (vol: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Map volume to size (10-200px)
    const size = Math.max(20, Math.min(300, vol * 3));

    // Random position
    const x = Math.random() * (canvas.width - size);
    const y = Math.random() * (canvas.height - size);

    // Draw letter in brand-green color (matches the design system).
    ctx.save();
    ctx.font = `600 ${size}px "Fraunces", Georgia, serif`;
    ctx.fillStyle = `rgba(66, 165, 122, ${Math.min(0.8, vol / 100)})`; // VDC_BRAND_GREEN
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(letter, x + size / 2, y + size / 2);
    ctx.restore();
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (drawMode === 'trace') {
      drawLetterPlaceholder();
    }
  };

  const drawLetterPlaceholder = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Draw large letter outline in center — Fraunces serif outline in
    // soft gold so the trace surface matches the rest of the system.
    const fontSize = Math.min(canvas.width, canvas.height) * 0.6;
    ctx.save();
    ctx.font = `600 ${fontSize}px "Fraunces", Georgia, serif`;
    ctx.strokeStyle = 'rgba(201, 169, 97, 0.5)'; // VDC_GOLD at ~50%
    ctx.lineWidth = 6;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.strokeText(letter, canvas.width / 2, canvas.height / 2);
    ctx.restore();
  };

  const getCoordinates = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();

    // Scale factors to account for canvas display size vs internal size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if ('touches' in e) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    } else {
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY
      };
    }
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (drawMode !== 'trace') return;

    e.preventDefault(); // Prevent scrolling on touch devices
    setIsDrawing(true);
    const { x, y } = getCoordinates(e);
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || drawMode !== 'trace') return;

    e.preventDefault(); // Prevent scrolling on touch devices
    const { x, y } = getCoordinates(e);
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    ctx.lineTo(x, y);
    ctx.strokeStyle = '#42a57a'; // VDC_BRAND_GREEN
    ctx.lineWidth = 14;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setRecordingBlob(blob);
        setShowCelebration(true);
        const congrats = language === 'en'
          ? `Amazing! ${animal} is so happy to meet you!`
          : `¡Increíble! ¡${animal} está muy feliz de conocerte!`;
        const funFactLabel = language === 'en' ? 'Fun fact' : 'Dato curioso';
        const toSpeak = funFact
          ? [congrats, `${funFactLabel}. ${funFact}`]
          : [congrats];
        speakSequence({ phrases: toSpeak, language, pauseMs: 600 });
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Recording failed:', err);
      alert(t.micPermission);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const playRecording = () => {
    if (!recordingBlob) return;
    if (playbackAudioRef.current) playbackAudioRef.current.pause();
    const url = URL.createObjectURL(recordingBlob);
    const audio = new Audio(url);
    playbackAudioRef.current = audio;
    audio.onended = () => URL.revokeObjectURL(url);
    audio.play();
  };

  const resetRecordState = () => {
    setShowCelebration(false);
    setRecordingBlob(null);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight - 200;

    clearCanvas();

    return () => {
      stopListening();
    };
  }, []);

  useEffect(() => {
    // Same single clip as alphabet grid (letter + animal + nature) for one consistent AI voice
    speakLetterAnimalAndNature(letter, animal, funFact ?? '', language);
  }, [letter, animal, language, funFact]);

  useEffect(() => {
    const idx = animalAlphabetData.findIndex((d) => d.letter === letter);
    if (idx < 0) return;
    const warm = (i: number) => {
      if (i < 0 || i >= animalAlphabetData.length) return;
      const item = animalAlphabetData[i];
      const a = language === 'en' ? item.animalEn : item.animalEs;
      const f = language === 'en' ? item.funFactEn : item.funFactEs;
      void prefetchLetterAnimalAndNature(item.letter, a, f, language);
    };
    warm(idx);
    warm(idx + 1);
    warm(idx - 1);
  }, [letter, language]);

  useEffect(() => {
    if (drawMode === 'trace') {
      clearCanvas();
    }
  }, [drawMode, letter]);

  useEffect(() => {
    resetRecordState();
  }, [letter, animal]);

  // AI voice-over for Greet your animal friend instructions
  useEffect(() => {
    if (drawMode === 'record' && emoji && !showCelebration) {
      playClickFeedback();
      const text =
        language === 'en'
          ? (t.recordVoiceEn as (l: string, a: string) => string)(letter, animal)
          : (t.recordVoiceEs as (l: string, a: string) => string)(letter, animal);
      speak({ text, language });
    }
  }, [drawMode, letter, animal, language, emoji]);

  // Reusable styles
  const modePill = (active: boolean): React.CSSProperties => ({
    padding: '12px 22px',
    borderRadius: 999,
    fontFamily: VDC_SANS,
    fontSize: 16,
    fontWeight: 600,
    border: `1.5px solid ${VDC_BRAND_GREEN}`,
    background: active ? VDC_BRAND_GREEN : 'transparent',
    color: active ? '#ffffff' : VDC_BRAND_GREEN,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    transition: 'background 160ms ease, color 160ms ease',
  });
  const orderPill = (active: boolean): React.CSSProperties => ({
    padding: '8px 18px',
    borderRadius: 999,
    fontFamily: VDC_SANS,
    fontSize: 14,
    fontWeight: 600,
    border: `1.5px solid ${VDC_BRAND_GREEN}`,
    background: active ? VDC_BRAND_GREEN : 'transparent',
    color: active ? '#ffffff' : VDC_BRAND_GREEN,
    cursor: 'pointer',
    transition: 'background 160ms ease, color 160ms ease',
  });
  const primaryPill: React.CSSProperties = {
    padding: '14px 28px',
    borderRadius: 999,
    fontFamily: VDC_SANS,
    fontSize: 18,
    fontWeight: 600,
    background: VDC_BRAND_GREEN,
    color: '#ffffff',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 6px 22px rgba(66,165,122,0.28)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
  };
  const outlinePill: React.CSSProperties = {
    padding: '14px 26px',
    borderRadius: 999,
    fontFamily: VDC_SANS,
    fontSize: 18,
    fontWeight: 600,
    background: 'transparent',
    color: VDC_BRAND_GREEN,
    border: `1.5px solid ${VDC_BRAND_GREEN}`,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
  };
  const dangerPill: React.CSSProperties = {
    ...primaryPill,
    background: VDC_SOFT_RED,
    boxShadow: '0 6px 22px rgba(184,90,85,0.28)',
  };
  const cardSurface: React.CSSProperties = {
    background: '#ffffff',
    border: `1px solid ${VDC_CARD_BORDER}`,
    borderRadius: 24,
  };

  const headline =
    drawMode === 'trace' ? t.traceTitle : drawMode === 'record' ? t.recordMode : t.voiceTitle;
  const instructions =
    drawMode === 'trace'
      ? t.traceInstructions
      : drawMode === 'voice'
        ? t.voiceInstructions
        : t.recordPrompt;
  const pastel = pastelForLetter(letter);

  return (
    <div className="min-h-screen p-4 md:p-6" style={{ background: VDC_CREAM }}>
      <div className="max-w-5xl mx-auto">
        {/* ── Header card ────────────────────────────────────────────── */}
        <div className="p-5 md:p-7 mb-5" style={cardSurface}>
          {/* Mode toggle pills */}
          <div className="flex gap-2 justify-center mb-6 flex-wrap">
            <button
              onClick={() => {
                setDrawMode('trace');
                if (isListening) stopListening();
              }}
              style={modePill(drawMode === 'trace')}
            >
              <Pencil className="w-4 h-4" />
              {t.traceMode}
            </button>
            <button onClick={() => setDrawMode('voice')} style={modePill(drawMode === 'voice')}>
              <Mic className="w-4 h-4" />
              {t.voiceMode}
            </button>
            {emoji && (
              <button
                onClick={() => {
                  if (drawMode === 'record') resetRecordState();
                  setDrawMode('record');
                  if (isListening) stopListening();
                }}
                style={modePill(drawMode === 'record')}
              >
                <Mic2 className="w-4 h-4" />
                {t.recordMode}
              </button>
            )}
          </div>

          {/* Title + instructions */}
          <h1
            className="text-center"
            style={{
              fontFamily: VDC_SERIF,
              fontWeight: 300,
              fontSize: 'clamp(36px, 5.5vw, 56px)',
              color: VDC_INK,
              letterSpacing: '-0.015em',
              lineHeight: 1.1,
              marginBottom: 8,
            }}
          >
            {headline}
          </h1>
          <p
            className="text-center mb-5"
            style={{
              fontFamily: VDC_SERIF,
              fontStyle: 'italic',
              fontWeight: 500,
              fontSize: 'clamp(16px, 2.2vw, 22px)',
              color: VDC_INK_SOFT,
              maxWidth: 580,
              margin: '0 auto 20px',
            }}
          >
            {instructions}
          </p>

          {/* A–Z / Random order toggle */}
          {onLetterOrderChange && letterOrder !== undefined && (
            <div className="flex gap-2 justify-center mb-5 flex-wrap">
              <button
                onClick={() => onLetterOrderChange('alphabetical')}
                style={orderPill(letterOrder === 'alphabetical')}
              >
                A–Z · {t.alphabetical}
              </button>
              <button
                onClick={() => onLetterOrderChange('random')}
                style={orderPill(letterOrder === 'random')}
              >
                {t.random}
              </button>
            </div>
          )}

          {/* Letter + emoji + name display — pastel tile pattern from
              AnimalAlphabetGrid so this screen feels like a sibling. */}
          <div className="text-center">
            <div
              className="inline-block"
              style={{
                background: '#ffffff',
                border: `1px solid ${VDC_CARD_BORDER}`,
                borderRadius: 18,
                padding: 14,
              }}
            >
              <div
                style={{
                  background: pastel,
                  borderRadius: 14,
                  padding: '24px 32px 16px',
                  textAlign: 'center',
                }}
              >
                <span
                  className="block"
                  style={{
                    fontFamily: VDC_SERIF,
                    fontSize: 'clamp(96px, 16vw, 156px)',
                    fontWeight: 600,
                    color: VDC_INK,
                    lineHeight: 1,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {letter}
                </span>
                {emoji && (
                  <span
                    className="block"
                    style={{
                      fontSize: 'clamp(48px, 8vw, 64px)',
                      lineHeight: 1,
                      marginTop: 14,
                      animation: 'float 3s ease-in-out infinite',
                    }}
                  >
                    {emoji}
                  </span>
                )}
              </div>
              <div
                style={{
                  fontFamily: VDC_SERIF,
                  fontStyle: 'italic',
                  fontSize: 'clamp(20px, 3vw, 26px)',
                  fontWeight: 600,
                  color: VDC_INK,
                  marginTop: 14,
                }}
              >
                {animal}
              </div>
            </div>
          </div>
        </div>

        {/* ── Record-mode start/stop ─────────────────────────────────── */}
        {drawMode === 'record' && !showCelebration && (
          <div className="flex gap-3 mb-5 justify-center flex-wrap">
            {!isRecording ? (
              <button onClick={startRecording} style={primaryPill}>
                <Mic2 className="w-5 h-5" />
                {t.startRecord}
              </button>
            ) : (
              <button onClick={stopRecording} style={{ ...dangerPill, animation: 'wp-rec-pulse 1.4s ease-in-out infinite' }}>
                <MicOff className="w-5 h-5" />
                {t.stopRecord}
              </button>
            )}
          </div>
        )}

        {/* ── Celebration overlay ─────────────────────────────────────── */}
        <AnimatePresence>
          {showCelebration && emoji && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              style={{ background: 'rgba(31,77,62,0.55)' }}
              onClick={(e) => e.target === e.currentTarget && resetRecordState()}
            >
              <motion.div
                initial={{ scale: 0.94, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 220, damping: 22 }}
                className="text-center max-w-lg w-full p-8 md:p-10"
                style={{
                  background: '#ffffff',
                  border: `1px solid ${VDC_GOLD}55`,
                  borderRadius: 24,
                  boxShadow: '0 18px 54px rgba(31,77,62,0.22)',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{
                    background: pastel,
                    borderRadius: 18,
                    padding: '28px 16px',
                    marginBottom: 20,
                  }}
                >
                  <div style={{ fontSize: 96, lineHeight: 1, animation: 'float 2.4s ease-in-out infinite' }}>
                    {emoji}
                  </div>
                </div>
                <h2
                  style={{
                    fontFamily: VDC_SERIF,
                    fontWeight: 300,
                    fontSize: 'clamp(28px, 5vw, 40px)',
                    color: VDC_INK,
                    letterSpacing: '-0.015em',
                    marginBottom: 8,
                  }}
                >
                  {t.celebrating}
                </h2>
                <p
                  style={{
                    fontFamily: VDC_SERIF,
                    fontStyle: 'italic',
                    fontWeight: 500,
                    fontSize: 'clamp(18px, 2.6vw, 24px)',
                    color: VDC_INK,
                    marginBottom: 6,
                  }}
                >
                  {animal} {t.animalHappy}
                </p>
                <p style={{ fontFamily: VDC_SANS, fontSize: 14, fontWeight: 500, color: VDC_MUTED, marginBottom: 22 }}>
                  {t.listenToYou}
                </p>
                {funFact && (
                  <div
                    className="mb-6 p-4 text-left"
                    style={{
                      background: VDC_CREAM_DEEP,
                      border: `1px solid ${VDC_GOLD}55`,
                      borderRadius: 14,
                    }}
                  >
                    <p
                      className="inline-flex items-center gap-1.5"
                      style={{
                        fontFamily: VDC_SANS,
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: '0.16em',
                        textTransform: 'uppercase',
                        color: VDC_MUTED,
                        marginBottom: 4,
                      }}
                    >
                      <Sparkles className="w-3 h-3" style={{ color: VDC_BRAND_GREEN }} />
                      {t.funFactLabel}
                    </p>
                    <p
                      style={{
                        fontFamily: VDC_SERIF,
                        fontStyle: 'italic',
                        fontWeight: 500,
                        fontSize: 16,
                        color: VDC_INK,
                        lineHeight: 1.5,
                      }}
                    >
                      {funFact}
                    </p>
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button onClick={playRecording} style={primaryPill}>
                    <Play className="w-5 h-5" />
                    {t.playRecording}
                  </button>
                  <button
                    onClick={() => {
                      resetRecordState();
                      startRecording();
                    }}
                    style={outlinePill}
                  >
                    <Mic2 className="w-5 h-5" />
                    {t.recordAgain}
                  </button>
                </div>
                <button
                  onClick={resetRecordState}
                  className="mt-5"
                  style={{
                    background: 'transparent',
                    color: VDC_MUTED,
                    border: 'none',
                    fontFamily: VDC_SANS,
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                  }}
                >
                  {t.close}
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Action controls ──────────────────────────────────────────── */}
        <div className="flex gap-3 mb-5 justify-center flex-wrap">
          {drawMode === 'voice' && (
            !isListening ? (
              <button onClick={startListening} style={primaryPill}>
                <Mic className="w-5 h-5" />
                {t.startMic}
              </button>
            ) : (
              <button onClick={stopListening} style={{ ...dangerPill, animation: 'wp-rec-pulse 1.4s ease-in-out infinite' }}>
                <MicOff className="w-5 h-5" />
                {t.stopMic}
              </button>
            )
          )}

          {(drawMode === 'trace' || drawMode === 'voice') && (
            <button onClick={clearCanvas} style={outlinePill}>
              <Eraser className="w-4 h-4" />
              {t.clear}
            </button>
          )}

          {onNext && (
            <button
              onClick={() => {
                clearCanvas();
                onNext();
              }}
              style={primaryPill}
            >
              <Sparkles className="w-5 h-5" />
              {t.newLetter}
            </button>
          )}

          <button onClick={onBack} style={outlinePill}>
            <Undo2 className="w-4 h-4" />
            {t.back}
          </button>
        </div>

        {/* ── Volume indicator (voice mode only) ──────────────────────── */}
        {drawMode === 'voice' && isListening && (
          <div className="p-4 mb-5" style={cardSurface}>
            <div className="flex items-center gap-4">
              <span
                style={{
                  fontFamily: VDC_SANS,
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: VDC_MUTED,
                }}
              >
                {language === 'es' ? 'Volumen' : 'Volume'}
              </span>
              <div
                className="flex-1 overflow-hidden"
                style={{
                  background: VDC_CREAM_DEEP,
                  borderRadius: 999,
                  height: 10,
                  border: `1px solid ${VDC_GOLD}33`,
                }}
              >
                <div
                  className="h-full transition-all duration-100"
                  style={{
                    background: VDC_BRAND_GREEN,
                    width: `${volume}%`,
                    borderRadius: 999,
                  }}
                />
              </div>
              <span
                className="w-12 text-right"
                style={{
                  fontFamily: VDC_SERIF,
                  fontWeight: 600,
                  fontSize: 18,
                  color: VDC_INK,
                }}
              >
                {Math.round(volume)}%
              </span>
            </div>
          </div>
        )}

        {/* ── Canvas (trace + voice modes) ─────────────────────────────── */}
        {drawMode !== 'record' && (
          <div className="p-4" style={cardSurface}>
            <canvas
              ref={canvasRef}
              className="w-full cursor-crosshair"
              style={{
                background: VDC_CREAM,
                border: `1px solid ${VDC_GOLD}55`,
                borderRadius: 18,
              }}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
          </div>
        )}

        {/* ── Record-mode placeholder card ─────────────────────────────── */}
        {drawMode === 'record' && !showCelebration && (
          <div className="p-8 text-center" style={cardSurface}>
            <div
              style={{
                background: pastel,
                borderRadius: 18,
                padding: '36px 16px 28px',
                marginBottom: 20,
                display: 'inline-block',
                minWidth: 220,
              }}
            >
              <div style={{ fontSize: 96, lineHeight: 1, animation: 'float 3s ease-in-out infinite' }}>
                {emoji || '🐾'}
              </div>
            </div>
            <p
              style={{
                fontFamily: VDC_SERIF,
                fontStyle: 'italic',
                fontWeight: 600,
                fontSize: 'clamp(22px, 3vw, 28px)',
                color: VDC_INK,
                marginBottom: 8,
              }}
            >
              {letter} — {animal}
            </p>
            <p
              style={{
                fontFamily: VDC_SERIF,
                fontStyle: 'italic',
                fontWeight: 500,
                fontSize: 16,
                color: VDC_INK_SOFT,
                maxWidth: 460,
                margin: '0 auto',
              }}
            >
              {t.recordPrompt}
            </p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        @keyframes wp-rec-pulse {
          0%, 100% { box-shadow: 0 6px 22px rgba(184,90,85,0.28); }
          50%      { box-shadow: 0 6px 22px rgba(184,90,85,0.55), 0 0 0 6px rgba(184,90,85,0.15); }
        }
      `}</style>
    </div>
  );
}
